import {
  DIFF_STYLE,
  type DiffEntry,
  type DiffResult,
  type DiffSide,
  type DrawingDocument,
  type DrawingEntity,
} from '@drawing2d/core';
import { t, type Lang } from '../i18n.js';

/**
 * One change, shown from both versions at once.
 *
 * A reviewer asked to accept a change needs to find it in *both* drawings, so
 * this is two columns and never one: the before column and the after column,
 * each reading from its own document. Where a side does not exist — an addition
 * has no before, a deletion has no after — the column says so in words. An empty
 * column would look like a rendering fault, and the difference between "there is
 * nothing there" and "we did not draw it" is the whole content of added and
 * removed.
 *
 * The lookup is by (which version, which page, which identifier). Entity
 * identifiers are unique inside one document and *collide across two*: both
 * versions contain "p0-1" and they are different entities. Keying anything here
 * on the identifier alone would show the reviewer the wrong entity while looking
 * entirely correct.
 *
 * Displacement is printed with its unit. At unknown scale the comparison runs in
 * normalised page units, and a bare number would be read as millimetres.
 */

export function EntityDetail({
  entry,
  entryIndex,
  diff,
  base,
  compare,
  lang,
}: {
  entry: DiffEntry;
  entryIndex: number;
  diff: DiffResult;
  base: DrawingDocument;
  compare: DrawingDocument;
  lang: Lang;
}): JSX.Element {
  const s = (k: Parameters<typeof t>[0]): string => t(k, lang);
  const unit = diff.displacementUnit === 'mm' ? 'mm' : s('unitPageDiagonal');

  return (
    <section className="panel entity-detail" data-entry={entryIndex}>
      <h2>{s('selectedChange')}</h2>

      <ul className="counts">
        <li data-fact="change-class">
          <span>{s('changeClassLabel')}</span>
          <b data-change-class={entry.changeClass}>{DIFF_STYLE[entry.changeClass].label[lang]}</b>
        </li>
        <li data-fact="entity-type">
          <span>{s('typeLabel')}</span>
          <b>{entry.entityType}</b>
        </li>
        {entry.displacement !== undefined && (
          <li data-fact="displacement">
            <span>{s('displacementLabel')}</span>
            <b>
              {entry.displacement.toFixed(4)} {unit}
            </b>
          </li>
        )}
      </ul>
      {entry.detail && <p className="note detail-text">{entry.detail}</p>}

      {entry.candidates && entry.candidates.length > 0 && (
        <div className="candidates" data-candidates={entry.candidates.length}>
          <h3>{s('candidatesLabel')}</h3>
          <ul className="counts">
            {entry.candidates.map((c) => (
              <li key={`${c.pageIndex}-${c.entityId}`}>
                <span>
                  {s('pageLabel')} {c.pageIndex + 1}
                </span>
                <b>{c.entityId}</b>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="two-versions">
        <SideColumn
          which="base"
          heading={s('beforeVersion')}
          side={entry.base}
          document={base}
          lang={lang}
        />
        <SideColumn
          which="compare"
          heading={s('afterVersion')}
          side={entry.compare}
          document={compare}
          lang={lang}
        />
      </div>
      <p className="note">{s('idCollisionNote')}</p>
    </section>
  );
}

function SideColumn({
  which,
  heading,
  side,
  document: doc,
  lang,
}: {
  which: 'base' | 'compare';
  heading: string;
  side: DiffSide | undefined;
  document: DrawingDocument;
  lang: Lang;
}): JSX.Element {
  const s = (k: Parameters<typeof t>[0]): string => t(k, lang);

  if (!side) {
    return (
      <div className="side" data-side={which} data-present="no">
        <h3>{heading}</h3>
        <p className="absent">{s('notInThisVersion')}</p>
      </div>
    );
  }

  const entity = entityAt(doc, side.pageIndex, side.entityId);

  return (
    <div className="side" data-side={which} data-present="yes">
      <h3>{heading}</h3>
      <ul className="counts">
        <li data-field="page">
          <span>{s('pageLabel')}</span>
          <b data-page-index={side.pageIndex}>{side.pageIndex + 1}</b>
        </li>
        <li data-field="entity-id">
          <span>{s('entityIdLabel')}</span>
          <b>{side.entityId}</b>
        </li>
        <li data-field="bbox">
          <span>{s('bboxLabel')}</span>
          <b>{side.bbox.map((n) => n.toFixed(2)).join(', ')}</b>
        </li>
      </ul>

      {entity === null ? (
        <p className="absent">{s('entityMissing')}</p>
      ) : (
        <>
          <h4>{s('propertiesLabel')}</h4>
          <ul className="counts">
            <li data-field="type">
              <span>{s('typeLabel')}</span>
              <b>{entity.type}</b>
            </li>
            <li data-field="layer">
              <span>{s('layerLabel')}</span>
              <b>{entity.layer}</b>
            </li>
            <li data-field="colour">
              <span>{s('colourLabel')}</span>
              <b>
                <i className="swatch" style={{ background: `#${entity.colour}` }} />#
                {entity.colour}
              </b>
            </li>
            <li data-field="stroke-width">
              <span>{s('strokeWidthLabel')}</span>
              <b>{entity.strokeWidth}</b>
            </li>
            <li data-field="geometry">
              <span>{s('geometryLabel')}</span>
              <b>{geometryOf(entity)}</b>
            </li>
          </ul>

          <h4>{s('provenanceLabel')}</h4>
          <ul className="counts">
            <li data-field="origin">
              <span>{s('originLabel')}</span>
              <b>{entity.origin}</b>
            </li>
            <li data-field="confidence">
              <span>{s('confidenceLabel')}</span>
              <b>{entity.confidence === undefined ? s('confidenceNone') : entity.confidence.toFixed(3)}</b>
            </li>
            <li data-field="bundle">
              <span>{s('bundleLabel')}</span>
              <b>{doc.provenance.extractorBundleVersion}</b>
            </li>
            <li data-field="scale-state">
              <span>{s('scaleStateLabel')}</span>
              <b>{doc.scaleState}</b>
            </li>
            <li data-field="libraries">
              <span>{s('librariesLabel')}</span>
              <b>
                {Object.entries(doc.provenance.libraryVersions)
                  .map(([name, version]) => `${name} ${version}`)
                  .join(' · ')}
              </b>
            </li>
          </ul>
        </>
      )}
    </div>
  );
}

/** The triple lookup. Never by identifier alone; see the module comment. */
function entityAt(doc: DrawingDocument, pageIndex: number, entityId: string): DrawingEntity | null {
  const page = doc.pages.find((p) => p.index === pageIndex);
  return page?.entities.find((e) => e.id === entityId) ?? null;
}

/** The properties that belong to this entity's own type, and no others. */
function geometryOf(e: DrawingEntity): string {
  const n = (v: number): string => v.toFixed(2);
  switch (e.type) {
    case 'LINE':
      return `(${n(e.start.x)}, ${n(e.start.y)}) → (${n(e.end.x)}, ${n(e.end.y)})`;
    case 'LWPOLYLINE': {
      const first = e.vertices[0];
      const last = e.vertices[e.vertices.length - 1];
      const ends =
        first && last ? ` (${n(first.x)}, ${n(first.y)}) … (${n(last.x)}, ${n(last.y)})` : '';
      const bulges = e.bulges.filter((b) => b !== 0).length;
      return `${e.vertices.length} vertices, ${e.closed ? 'closed' : 'open'}, ${bulges} bulged${ends}`;
    }
    case 'CIRCLE':
      return `centre (${n(e.centre.x)}, ${n(e.centre.y)}), r ${n(e.radius)}`;
    case 'ARC':
      return `centre (${n(e.centre.x)}, ${n(e.centre.y)}), r ${n(e.radius)}, ${n(e.startAngle)}° → ${n(e.endAngle)}°`;
    case 'TEXT':
    case 'MTEXT':
      return `“${e.text}” at (${n(e.anchor.x)}, ${n(e.anchor.y)}), height ${n(e.height)}, rotation ${n(e.rotation)}°`;
  }
}
